import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { Edit2, Trash2, Plus, FileText, Globe } from 'lucide-react';
import { ForecastTemplate, FORECAST_TYPE_LABELS, SupportedLang, ForecastType } from '../../data/forecastCmsData';
interface AdminForecastTemplatesManagerProps {
  templates: ForecastTemplate[];
  setTemplates: React.Dispatch<React.SetStateAction<ForecastTemplate[]>>;
  success: (msg: string) => void;
  renderTableHeader: (title: string, count: number, onExport?: () => void, onCreate?: () => void, createLabel?: string) => React.ReactNode;
}
export function AdminForecastTemplatesManager({
  templates,
  setTemplates,
  success,
  renderTableHeader
}: AdminForecastTemplatesManagerProps) {
  const [editingTemplate, setEditingTemplate] = useState<ForecastTemplate | null>(null);
  const handleDelete = (id: string) => {
    if (confirm('Удалить шаблон? Это не повлияет на уже созданные прогнозы.')) {
      setTemplates(templates.filter(t => t.id !== id));
      success('Шаблон удален');
    }
  };
  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTemplate) return;
    if (templates.some(t => t.id === editingTemplate.id)) {
      setTemplates(templates.map(t => t.id === editingTemplate.id ? {
        ...editingTemplate,
        updated_at: new Date().toISOString()
      } : t));
      success('Шаблон обновлен');
    } else {
      setTemplates([editingTemplate, ...templates]);
      success('Шаблон создан');
    }
    setEditingTemplate(null);
  };
  const handleCreateNew = () => {
    setEditingTemplate({
      id: `tpl-${Date.now()}`,
      title: 'Новый шаблон',
      forecast_type: 'day',
      lang: 'ru',
      required_fields: ['general'],
      content_blocks: ['Общий прогноз'],
      generation_rules: 'Тон: дружелюбный.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
  };
  if (editingTemplate) {
    return <div className="space-y-6" data-id="element-4439">
        <div className="flex items-center justify-between" data-id="element-4440">
          <div data-id="element-4441">
            <h2 className="text-2xl font-bold text-slate-900" data-id="element-4442">
              {templates.some(t => t.id === editingTemplate.id) ? 'Редактирование шаблона' : 'Новый шаблон'}
            </h2>
          </div>
          <div className="flex items-center gap-3" data-id="element-4443">
            <Button variant="outline" onClick={() => setEditingTemplate(null)} data-id="element-4444">
              Отмена
            </Button>
            <Button variant="primary" onClick={handleSaveEdit} data-id="element-4445">
              Сохранить
            </Button>
          </div>
        </div>

        <Card padding="lg" className="space-y-6 max-w-3xl" data-id="element-4446">
          <div className="space-y-4" data-id="element-4447">
            <div data-id="element-4448">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4449">
                Название шаблона *
              </label>
              <Input value={editingTemplate.title} onChange={e => setEditingTemplate({
              ...editingTemplate,
              title: e.target.value
            })} data-id="element-4450" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4451">
              <div data-id="element-4452">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4453">
                  Тип прогноза *
                </label>
                <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={editingTemplate.forecast_type} onChange={e => setEditingTemplate({
                ...editingTemplate,
                forecast_type: e.target.value as ForecastType
              })} data-id="element-4454">
                  {Object.entries(FORECAST_TYPE_LABELS).map(([key, val]) => <option key={key} value={key} data-id="element-4455">
                      {val.ru}
                    </option>)}
                </select>
              </div>
              <div data-id="element-4456">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4457">
                  Язык *
                </label>
                <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={editingTemplate.lang} onChange={e => setEditingTemplate({
                ...editingTemplate,
                lang: e.target.value as SupportedLang
              })} data-id="element-4458">
                  <option value="ru" data-id="element-4459">Русский</option>
                  <option value="en" data-id="element-4460">English</option>
                </select>
              </div>
            </div>

            <div data-id="element-4461">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4462">
                Блоки контента (через запятую)
              </label>
              <Input value={editingTemplate.content_blocks.join(', ')} onChange={e => setEditingTemplate({
              ...editingTemplate,
              content_blocks: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
            })} data-id="element-4463" />
            </div>

            <div data-id="element-4464">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4465">
                Правила для AI генерации (Промпт)
              </label>
              <textarea className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[150px]" value={editingTemplate.generation_rules} onChange={e => setEditingTemplate({
              ...editingTemplate,
              generation_rules: e.target.value
            })} placeholder="Опишите тон, стиль, длину и правила для нейросети..." data-id="element-4466" />
            </div>
          </div>
        </Card>
      </div>;
  }
  return <div className="space-y-6" data-id="element-4467">
      {renderTableHeader('Шаблоны прогнозов', templates.length, undefined, handleCreateNew, 'Создать шаблон')}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4468">
        {templates.map(template => <Card key={template.id} padding="lg" className="flex flex-col h-full" data-id="element-4469">
            <div className="flex items-start justify-between mb-4" data-id="element-4470">
              <div className="flex items-center gap-3" data-id="element-4471">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600" data-id="element-4472">
                  <FileText className="w-5 h-5" data-id="element-4473" />
                </div>
                <div data-id="element-4474">
                  <h3 className="font-bold text-slate-900" data-id="element-4475">{template.title}</h3>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-1" data-id="element-4476">
                    <Badge variant="neutral" size="sm" data-id="element-4477">
                      {FORECAST_TYPE_LABELS[template.forecast_type].ru}
                    </Badge>
                    <span className="flex items-center gap-1" data-id="element-4478">
                      <Globe className="w-3 h-3" data-id="element-4479" /> {template.lang}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 mb-6" data-id="element-4480">
              <p className="text-sm text-slate-600 line-clamp-3 mb-3" data-id="element-4481">
                <span className="font-medium text-slate-900" data-id="element-4482">Правила AI:</span>{' '}
                {template.generation_rules}
              </p>
              <div className="flex flex-wrap gap-1" data-id="element-4483">
                {template.content_blocks.map((block, i) => <span key={i} className="text-[10px] px-2 py-1 bg-slate-100 text-slate-600 rounded" data-id="element-4484">
                    {block}
                  </span>)}
              </div>
            </div>

            <div className="flex items-center gap-2 mt-auto pt-4 border-t border-slate-100" data-id="element-4485">
              <Button variant="outline" className="flex-1" onClick={() => setEditingTemplate(template)} data-id="element-4486">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-4487" /> Редактировать
              </Button>
              <Button variant="ghost" className="px-3 text-rose-500 hover:bg-rose-50" onClick={() => handleDelete(template.id)} data-id="element-4488">
                <Trash2 className="w-4 h-4" data-id="element-4489" />
              </Button>
            </div>
          </Card>)}
      </div>
    </div>;
}